'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/lib/AuthContext';
import { OperatorSkullIcon } from '@/components/icons/OperatorSkullIcon';
import { Menu, Dumbbell } from 'lucide-react';
import OfflineBanner from '@/components/OfflineBanner';
import DestinationIcon from '@/components/nav/DestinationIcon';
import { useUiPreferences } from '@/lib/hooks/useUiPreferences';
import { getDestination } from '@/lib/uiPreferences';

export default function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, signOut } = useAuth();
  const { prefs } = useUiPreferences();

  // Don't show header/nav on login, legal, or onboarding pages.
  // Onboarding intentionally hides the bottom tab bar so users can't
  // accidentally tap Home/Stats/Workout mid-flow and abandon setup.
  const isAuthPage =
    pathname === '/login' ||
    pathname === '/forgot-password' ||
    pathname === '/privacy' ||
    pathname === '/terms' ||
    pathname === '/onboarding' ||
    pathname === '/onboarding-v2' ||
    pathname.startsWith('/onboarding/') ||
    pathname.startsWith('/onboarding-v2/');

  // Bottom bar layout (POC D — tactical FAB):
  //   [Home] [tab #1] [FAB: Start Workout] [tab #2] [More]
  // The FAB lifts above the bar and routes to /session. Two flanking
  // user-customizable tabs (instead of the prior three) so the bar
  // stays balanced around the centerpiece.
  //
  // Filter /session out — the FAB already owns it. Users with saved
  // prefs from before the FAB existed had /session in their tab list;
  // without this filter they'd see WORKOUT twice (tab + FAB).
  const navTabs = prefs.navTabs
    .filter(k => k !== '/session')
    .map(getDestination)
    .filter((d): d is NonNullable<typeof d> => !!d)
    .slice(0, 2);

  const workoutActive = pathname === '/session' || pathname.startsWith('/session/');

  const handleLogout = async () => {
    try {
      await signOut();
      // Redirect handled by ProtectedRoute
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isAuthPage) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen flex flex-col pb-20">
      <OfflineBanner />
      {/* Skip to content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-operator-gold focus:text-operator-gunmetal focus:rounded focus:font-bold"
      >
        Skip to content
      </a>

      {/* Header - Midnight Operations Style */}
      <header className="border-b border-operator-steel bg-operator-slate sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto px-4 py-2 sm:py-3 flex items-center justify-between">
          <Link href="/" className="flex items-center hover:opacity-95 transition">
            <img
              src="/opmode-full.svg"
              alt="OPMODE Logo"
              className="h-10 sm:h-14 w-auto object-contain drop-shadow-[0_0_12px_rgba(0,0,0,0.8)]"
              onError={(e) => {
                console.error('Logo failed to load');
                e.currentTarget.style.display = 'none';
              }}
            />
          </Link>
          {/* Logout moved to Settings page */}
        </div>
      </header>

      {/* Main Content - Extra padding bottom for tab bar */}
      <main id="main-content" className="flex-1 container mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {children}
      </main>

      {/* Bug Report Button - Rendered via ErrorLoggerInit in layout.tsx */}

      {/* Bottom Tab Navigation — POC D (tactical center FAB).
          Five-column grid with the middle column reserved as a spacer;
          the FAB lifts above the bar via absolute positioning. */}
      <div className="fixed bottom-0 left-0 right-0 z-50 pb-[env(safe-area-inset-bottom)]">
        <nav
          className="relative bg-operator-slate border-t-2 border-operator-steel shadow-2xl"
          aria-label="Main navigation"
        >
          {/* Center FAB — primary action, always routes to /session */}
          <Link
            href="/session"
            aria-label="Start workout"
            aria-current={workoutActive ? 'page' : undefined}
            className={`absolute left-1/2 -translate-x-1/2 -top-6 w-16 h-16 rounded-full flex flex-col items-center justify-center transition-all duration-200 active:scale-[0.94] z-10 ${
              workoutActive
                ? 'bg-gradient-to-br from-operator-gold to-amber-500 text-operator-void shadow-[0_0_28px_rgba(245,158,11,0.7),0_4px_18px_rgba(0,0,0,0.6)] ring-2 ring-operator-gold/40'
                : 'bg-operator-gunmetal border-2 border-operator-gold/70 text-operator-gold shadow-[0_0_18px_rgba(245,158,11,0.35),0_4px_18px_rgba(0,0,0,0.6)] hover:border-operator-gold hover:shadow-[0_0_24px_rgba(245,158,11,0.55),0_4px_18px_rgba(0,0,0,0.6)]'
            }`}
          >
            <Dumbbell className="w-6 h-6" aria-hidden />
            <span className="text-[8px] font-heading font-bold uppercase tracking-[0.18em] mt-0.5">Start</span>
          </Link>

          <div className="container mx-auto">
            <div className="grid grid-cols-5 gap-0" role="tablist">
              {/* Home */}
              <Link
                href="/"
                role="tab"
                aria-selected={pathname === '/'}
                aria-label="Home"
                className={`flex flex-col items-center justify-center py-3 px-1 transition-all active:scale-[0.96] ${
                  pathname === '/'
                    ? 'text-operator-gold'
                    : 'text-gray-400 hover:text-operator-gold'
                }`}
              >
                <span className="mb-1" aria-hidden="true">
                  <OperatorSkullIcon className="w-6 h-6" />
                </span>
                <span className="text-[10px] font-heading font-semibold uppercase tracking-wide">Home</span>
              </Link>

              {/* User tab #1 (or empty cell if none) */}
              {navTabs[0] ? (() => {
                const d = navTabs[0];
                const active = pathname === d.key || pathname.startsWith(d.key + '/');
                return (
                  <Link
                    href={d.key}
                    role="tab"
                    aria-selected={active}
                    aria-label={d.label}
                    className={`flex flex-col items-center justify-center py-3 px-1 transition-all active:scale-[0.96] ${
                      active ? 'text-operator-gold' : 'text-gray-400 hover:text-operator-gold'
                    }`}
                  >
                    <span className="mb-1"><DestinationIcon name={d.icon} className="w-6 h-6" /></span>
                    <span className="text-[10px] font-heading font-semibold uppercase tracking-wide truncate max-w-full">{d.label}</span>
                  </Link>
                );
              })() : <div aria-hidden />}

              {/* Spacer column under the FAB */}
              <div aria-hidden />

              {/* User tab #2 (or empty cell if none) */}
              {navTabs[1] ? (() => {
                const d = navTabs[1];
                const active = pathname === d.key || pathname.startsWith(d.key + '/');
                return (
                  <Link
                    href={d.key}
                    role="tab"
                    aria-selected={active}
                    aria-label={d.label}
                    className={`flex flex-col items-center justify-center py-3 px-1 transition-all active:scale-[0.96] ${
                      active ? 'text-operator-gold' : 'text-gray-400 hover:text-operator-gold'
                    }`}
                  >
                    <span className="mb-1"><DestinationIcon name={d.icon} className="w-6 h-6" /></span>
                    <span className="text-[10px] font-heading font-semibold uppercase tracking-wide truncate max-w-full">{d.label}</span>
                  </Link>
                );
              })() : <div aria-hidden />}

              {/* More */}
              <Link
                href="/more"
                role="tab"
                aria-selected={pathname === '/more'}
                aria-label="More"
                className={`flex flex-col items-center justify-center py-3 px-1 transition-all active:scale-[0.96] ${
                  pathname === '/more'
                    ? 'text-operator-gold'
                    : 'text-gray-400 hover:text-operator-gold'
                }`}
              >
                <span className="mb-1" aria-hidden="true"><Menu className="w-6 h-6" /></span>
                <span className="text-[10px] font-heading font-semibold uppercase tracking-wide">More</span>
              </Link>
            </div>
          </div>
        </nav>
      </div>
    </div>
  );
}
